import { Sun, Moon, Monitor, ChevronDown, Settings } from "lucide-react";
import { User } from "../../types";
import { SidebarTrigger } from "../ui/sidebar";
import { CompanyLogo } from "../ui/company-logo";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { canAccessSettings, displayRoles } from "../../utils/accessControl";
import { navigateToView } from "../../utils/appRoutes";

interface HeaderProps {
  currentUser: User;
  currentTheme: "light" | "dark" | "auto";
  onThemeChange: (theme: "light" | "dark" | "auto") => void;
  isTransitioning?: boolean;
}

export function Header({
  currentUser,
  currentTheme,
  onThemeChange,
  isTransitioning,
}: HeaderProps) {
  const getUserInitials = () => {
    return (
      currentUser.name
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0]?.toUpperCase() ?? "")
        .join("") || "U"
    );
  };

  const getRoleLabel = () => {
    const labels = displayRoles(currentUser);
    if (labels.length) return labels.join(" · ");
    switch (currentUser.role) {
      case "admin":
        return "Administrator";
      case "supervisor":
        return "Supervisor";
      case "fabricator":
        return "Fabricator";
      case "client":
        return "Client";
      default:
        return "User";
    }
  };

  return (
    <header className="sticky top-0 z-20 flex min-h-[65px] items-center justify-between border-b border-border/70 bg-background/80 px-3.5 py-2.5 backdrop-blur-md transition-colors duration-200 sm:min-h-[73px] sm:px-6 sm:py-3">
      <div className="flex items-center gap-2 sm:gap-4">
        <SidebarTrigger className="hover:bg-accent/80 rounded-lg p-2 transition-colors cursor-pointer text-muted-foreground hover:text-foreground" />
        <div className="flex min-w-0 items-center xl:hidden">
          <CompanyLogo size="sm" clickable className="max-w-[clamp(7rem,42vw,10rem)]" />
        </div>
      </div>

      <div className="flex items-center gap-3 sm:gap-4">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="flex min-h-10 cursor-pointer items-center gap-3 rounded-lg border border-border/60 bg-card/40 px-3 py-1.5 outline-none transition-[background-color,border-color,color] duration-150 hover:bg-accent/60 focus-visible:ring-2 focus-visible:ring-ring">
              <div className="relative shrink-0">
                <div className="flex size-7 items-center justify-center rounded-md bg-primary text-primary-foreground font-semibold">
                  <span className="text-xs">{getUserInitials()}</span>
                </div>
                <span className="absolute -bottom-0.5 -right-0.5 size-2.5 rounded-full border-2 border-background bg-emerald-500" />
              </div>

              <div className="hidden min-w-0 flex-col items-start leading-tight sm:flex text-left">
                <p className="max-w-[130px] truncate text-xs font-semibold text-foreground">
                  {currentUser.name}
                </p>
                <p className="max-w-[130px] truncate text-[10px] text-muted-foreground font-mono uppercase tracking-wider">
                  {getRoleLabel()}
                </p>
              </div>

              <ChevronDown className="hidden h-3.5 w-3.5 text-muted-foreground sm:block opacity-60" />
            </button>
          </DropdownMenuTrigger>

          <DropdownMenuContent
            align="end"
            sideOffset={8}
            className="w-[260px] p-0"
          >
            <div className="flex items-center gap-3 border-b border-border/60 px-4 py-3 bg-muted/30">
              <div className="relative shrink-0">
                <div className="flex size-10 items-center justify-center rounded-lg bg-primary text-primary-foreground">
                  <span className="text-sm font-bold">{getUserInitials()}</span>
                </div>
                <span className="absolute -bottom-0.5 -right-0.5 size-3 rounded-full border-2 border-popover bg-emerald-500" />
              </div>
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-foreground">
                  {currentUser.name}
                </p>
                <p className="truncate text-xs text-muted-foreground font-mono">
                  {currentUser.email || getRoleLabel()}
                </p>
              </div>
            </div>

            {canAccessSettings(currentUser) && (
              <>
                <div className="p-1">
                  <DropdownMenuItem
                    onClick={() => navigateToView("settings")}
                    className="rounded-md px-3 py-2 text-xs font-medium cursor-pointer"
                  >
                    <Settings className="mr-2 h-3.5 w-3.5 text-muted-foreground" />
                    Profile & Settings
                  </DropdownMenuItem>
                </div>
                <DropdownMenuSeparator className="my-0" />
              </>
            )}

            <DropdownMenuLabel className="px-4 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              Interface Theme
            </DropdownMenuLabel>
            <DropdownMenuRadioGroup
              value={currentTheme}
              onValueChange={(value) =>
                onThemeChange(value as "light" | "dark" | "auto")
              }
              className="p-1"
            >
              <DropdownMenuRadioItem
                value="light"
                className="rounded-md px-3 py-1.5 text-xs font-medium cursor-pointer"
              >
                <Sun className="mr-2 h-3.5 w-3.5 text-amber-500" />
                Light
              </DropdownMenuRadioItem>
              <DropdownMenuRadioItem
                value="dark"
                className="rounded-md px-3 py-1.5 text-xs font-medium cursor-pointer"
              >
                <Moon className="mr-2 h-3.5 w-3.5 text-sky-400" />
                Dark
              </DropdownMenuRadioItem>
              <DropdownMenuRadioItem
                value="auto"
                className="rounded-md px-3 py-1.5 text-xs font-medium cursor-pointer"
              >
                <Monitor className="mr-2 h-3.5 w-3.5 text-muted-foreground" />
                Auto
              </DropdownMenuRadioItem>
            </DropdownMenuRadioGroup>
            {isTransitioning && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  disabled
                  className="mx-2 my-1.5 rounded-md px-3 py-1.5 text-xs text-muted-foreground"
                >
                  Applying theme...
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
